import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import type { Control, FieldValues, UseFormSetValue } from 'react-hook-form';

import type {
  IMasterDocumentAdapter,
  SerialRow,
} from '../../adapter/MasterDocumentAdapter.types';
import type { SerialSupportConfig } from '../../template/MasterDocumentTemplate.types';
import type { SerialPanelProps } from '../SerialPanel';
import { useSerialPanelTranslation } from '../i18n';

// ── Defaults de configuración ────────────────────────────────
const DEFAULT_TRIGGER_FIELD = '_itemIsSerialized';
const DEFAULT_QUANTITY_FIELD = 'quantity_base';
const DEFAULT_SERIAL_IDS_FIELD = 'serial_ids';
const DEFAULT_ITEM_FIELD = 'item_extension_id';
const DEFAULT_STORAGE_FIELD = 'storage_id';
const DEFAULT_LOT_FIELD = 'lot_id';

/** Tamaño de página de la consulta server-side de seriales (modo `select`). */
const SELECT_PAGE_SIZE = 8;
/** Debounce de la búsqueda/refetch server-side (ms). */
const FETCH_DEBOUNCE_MS = 250;

type ValidateSerialFn = NonNullable<IMasterDocumentAdapter['validateSerial']>;
type FetchAvailableSerialsFn = NonNullable<IMasterDocumentAdapter['fetchAvailableSerials']>;

export interface UseSerialPanelParams {
  /** `layout.detail.serialSupport` del template. Si se omite, el panel queda inactivo. */
  serialSupport?: SerialSupportConfig;
  /** `control` del formulario de la línea (react-hook-form). */
  control: Control<FieldValues>;
  /** `setValue` del formulario de la línea (para gestionar el campo virtual `serial_ids`). */
  setValue: UseFormSetValue<FieldValues>;
  /**
   * Solo lectura: el documento está fuera del estado editable. El contenedor
   * resuelve el criterio (mismo que usa `LineFormModal` para `editableOn`).
   */
  readOnly?: boolean;
  /**
   * Transición open→true del modal: re-siembra el estado del panel desde
   * `initialData` (precarga en edición) y resetea búsqueda/paginación.
   */
  open?: boolean;
  /** Datos iniciales de la línea (para precargar `serial_ids` en edición). */
  initialData?: Record<string, unknown>;
  /** `adapter.validateSerial` — requerido en modo `capture`. */
  validateSerial?: ValidateSerialFn;
  /** `adapter.fetchAvailableSerials` — requerido en modo `select`. */
  fetchAvailableSerials?: FetchAvailableSerialsFn;
}

export interface UseSerialPanelResult {
  /** `true` cuando el panel debe renderizarse (mode ≠ none y el ítem es serializado). */
  isActive: boolean;
  /**
   * `true` cuando el gating de Guardar está satisfecho:
   * `serial_ids.length === M` (o el panel está inactivo / no soportado).
   */
  isComplete: boolean;
  /** Cantidad requerida `M` (en vivo desde `quantityField`). */
  required: number;
  /** Seriales actualmente registrados en `serial_ids`. */
  count: number;
  /** Clave del campo virtual donde viajan los IDs de seriales. */
  serialIdsField: string;
  /** `true` cuando el adapter no implementa el método requerido por el `mode`. */
  unsupported: boolean;
  /** Mensaje a mostrar cuando `unsupported` es `true`. */
  unsupportedMessage: string | null;
  /** Props listos para `<SerialPanel {...panelProps} />`. `null` si no aplica. */
  panelProps: SerialPanelProps | null;
}

function toBool(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value) ? (value as unknown[]).map(String) : [];
}

/**
 * Hook contenedor del soporte nativo de seriales (FASE 2).
 *
 * Vive dentro de `LineFormModal`: lee `serialSupport`, observa el form de la
 * línea (trigger, cantidad, ítem, bodega, lote), gestiona el campo virtual
 * `serial_ids` y cablea el adapter (`validateSerial` para `capture`,
 * `fetchAvailableSerials` paginado server-side para `select`).
 *
 * Reglas:
 * - El panel solo se muestra cuando `triggerField` (default `_itemIsSerialized`)
 *   es truthy. Si no, no gatea Guardar (comportamiento idéntico al actual).
 * - La cantidad requerida `M` se lee en vivo de `quantityField`.
 * - Al cambiar la CANTIDAD se conservan los seriales (el badge revalida solo).
 * - Al cambiar el ÍTEM (`itemField`) se limpian los seriales.
 * - `readOnly` desactiva la edición fuera del estado editable.
 * - Degrada con un mensaje claro si el adapter no implementa el método del `mode`.
 *
 * @see SerialPanel — componente presentacional que recibe `panelProps`.
 */
export function useSerialPanel({
  serialSupport,
  control,
  setValue,
  readOnly = false,
  open = true,
  initialData,
  validateSerial,
  fetchAvailableSerials,
}: UseSerialPanelParams): UseSerialPanelResult {
  const { t } = useSerialPanelTranslation();

  const mode = serialSupport?.mode ?? 'none';
  const triggerField = serialSupport?.triggerField ?? DEFAULT_TRIGGER_FIELD;
  const quantityField = serialSupport?.quantityField ?? DEFAULT_QUANTITY_FIELD;
  const serialIdsField = serialSupport?.serialIdsField ?? DEFAULT_SERIAL_IDS_FIELD;
  const itemField = serialSupport?.itemField ?? DEFAULT_ITEM_FIELD;
  const storageField = serialSupport?.storageField ?? DEFAULT_STORAGE_FIELD;
  const lotField = serialSupport?.lotField ?? DEFAULT_LOT_FIELD;
  // Confirmación al marcar (modo `select`): viaja tal cual al panel, que es donde
  // vive la decisión, junto al `toggle`. El hook no añade lógica.
  const confirmOnStatuses = serialSupport?.confirmOnStatuses;
  const confirmMessageKey = serialSupport?.confirmMessageKey;

  // ── Observación en vivo del form ───────────────────────────
  const triggerValue = useWatch({ control, name: triggerField });
  const quantityValue = useWatch({ control, name: quantityField });
  const itemValue = useWatch({ control, name: itemField });
  const storageValue = useWatch({ control, name: storageField });
  const lotValue = useWatch({ control, name: lotField });
  const serialIdsValue = useWatch({ control, name: serialIdsField });

  const isActive = mode !== 'none' && toBool(triggerValue);
  const required = Math.max(0, Math.floor(Number(quantityValue) || 0));

  const serialIds = useMemo(() => toStringArray(serialIdsValue), [serialIdsValue]);
  const serialIdsRef = useRef<string[]>(serialIds);
  useEffect(() => {
    serialIdsRef.current = serialIds;
  }, [serialIds]);

  const itemExtensionId = itemValue == null ? '' : String(itemValue);
  const storageId = storageValue == null || storageValue === '' ? undefined : String(storageValue);
  const lotId = lotValue == null || lotValue === '' ? undefined : String(lotValue);

  // ── Estado de presentación (capture) ───────────────────────
  // Códigos legibles mostrados como chips, alineados índice a índice con serial_ids.
  const [captureCodes, setCaptureCodes] = useState<string[]>([]);
  const [captureError, setCaptureError] = useState<string | null>(null);

  // ── Estado de presentación (select) ────────────────────────
  const [selectRows, setSelectRows] = useState<SerialRow[]>([]);
  const [selectTotal, setSelectTotal] = useState(0);
  const [selectLoading, setSelectLoading] = useState(false);
  // Hay un `validateSerial` en vuelo (P5 de LOADING-INTEGRATION.md). El código
  // recién leído no entra en la lista hasta que el backend lo aprueba, así que
  // sin esto el operario pulsa Enter y no ve absolutamente nada.
  const [validating, setValidating] = useState(false);
  const [selectQuery, setSelectQuery] = useState('');
  const [selectPage, setSelectPage] = useState(1);
  const selectGenRef = useRef(0);

  const commitSerialIds = useCallback(
    (next: string[]) => {
      // Tope duro: el onChange del SerialSelectPanel nunca debe exceder `required`.
      // (El panel ya bloquea la sobre-selección con un toast; esto es defensa en
      // profundidad. No recortamos cuando required es 0/transitorio.)
      const capped = required > 0 && next.length > required ? next.slice(0, required) : next;
      serialIdsRef.current = capped;
      setValue(serialIdsField, capped, { shouldDirty: true });
    },
    [setValue, serialIdsField, required],
  );

  // ── Reinicio en la transición open→true (precarga en edición) ──
  const prevOpenRef = useRef(false);
  const prevItemRef = useRef<unknown>(undefined);
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;

    // Baseline del ítem desde initialData (no desde el useWatch, que aún está stale).
    prevItemRef.current = initialData?.[itemField];

    const seeded = toStringArray(initialData?.[serialIdsField]);
    serialIdsRef.current = seeded;
    // `reset()` del modal limpia serial_ids → re-inyectar sin marcar dirty.
    setValue(serialIdsField, seeded, { shouldDirty: false });
    // TODO(seriales): en EDICIÓN solo tenemos `serial_ids` (GUIDs), no el código
    // legible, así que los chips de captura muestran el GUID. Para mostrar el
    // código real el backend debe devolverlo en la precarga de la línea (p.ej.
    // un campo `serial_codes` paralelo a `serial_ids`). Follow-up fuera de este PR.
    setCaptureCodes(seeded);
    setCaptureError(null);
    setSelectQuery('');
    setSelectPage(1);
  }, [open, initialData, itemField, serialIdsField, setValue]);

  // ── Limpieza al cambiar el ÍTEM ────────────────────────────
  // Solo limpiamos ante un cambio REAL `valorA → valorB` con ambos definidos y
  // distintos. Las transiciones desde/hacia vacío (undefined/null/'') se tratan
  // como inicialización: cubren la carrera open↔reset en la que `useWatch` llega
  // `undefined` antes de que aplique el `reset()` del modal, lo que de otro modo
  // borraría los `serial_ids` recién precargados en edición.
  useEffect(() => {
    if (!open) return;
    const prev = prevItemRef.current;
    if (Object.is(prev, itemValue)) return;
    const isEmpty = (v: unknown) => v === undefined || v === null || v === '';
    if (isEmpty(prev) || isEmpty(itemValue)) {
      // Inicialización (o limpieza del campo): adoptar baseline sin tocar seriales.
      prevItemRef.current = itemValue;
      return;
    }
    // Cambio real de ítem → limpiar seriales.
    prevItemRef.current = itemValue;
    commitSerialIds([]);
    setCaptureCodes([]);
    setCaptureError(null);
    setSelectQuery('');
    setSelectPage(1);
  }, [itemValue, open, commitSerialIds]);

  // ── Degradación elegante ───────────────────────────────────
  const unsupported =
    isActive &&
    ((mode === 'capture' && !validateSerial) ||
      (mode === 'select' && !fetchAvailableSerials));

  const loggedUnsupportedRef = useRef(false);
  useEffect(() => {
    if (unsupported && !loggedUnsupportedRef.current) {
      loggedUnsupportedRef.current = true;
      const missing = mode === 'capture' ? 'validateSerial' : 'fetchAvailableSerials';
      console.error(
        `[MasterDocument] serialSupport.mode="${mode}" requiere que el adapter implemente "${missing}". ` +
          'El panel de seriales se deshabilita.',
      );
    }
    if (!unsupported) loggedUnsupportedRef.current = false;
  }, [unsupported, mode]);

  // ── Fetch server-side (modo select) ────────────────────────
  useEffect(() => {
    if (!isActive || mode !== 'select' || !fetchAvailableSerials) return;
    const handle = setTimeout(() => {
      const gen = ++selectGenRef.current;
      setSelectLoading(true);
      fetchAvailableSerials({
        itemExtensionId,
        storageId,
        lotId,
        status: 'Available',
        page: selectPage,
        pageSize: SELECT_PAGE_SIZE,
        q: selectQuery.trim() || undefined,
      })
        .then((res) => {
          if (selectGenRef.current !== gen) return;
          setSelectRows(res?.items ?? []);
          setSelectTotal(res?.total ?? 0);
        })
        .catch(() => {
          if (selectGenRef.current !== gen) return;
          setSelectRows([]);
          setSelectTotal(0);
        })
        .finally(() => {
          if (selectGenRef.current === gen) setSelectLoading(false);
        });
    }, FETCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [
    isActive,
    mode,
    fetchAvailableSerials,
    itemExtensionId,
    storageId,
    lotId,
    selectPage,
    selectQuery,
  ]);

  // ── Captura: validar cada serial nuevo contra el adapter ───
  const handleCaptureChange = useCallback(
    async (nextCodes: string[]) => {
      const currentCodes = captureCodes;

      // Eliminación: códigos son únicos → localizar el índice removido.
      if (nextCodes.length < currentCodes.length) {
        const nextSet = new Set(nextCodes);
        const removedIdx = currentCodes.findIndex((c) => !nextSet.has(c));
        const nextIds = serialIdsRef.current.slice();
        if (removedIdx >= 0) nextIds.splice(removedIdx, 1);
        setCaptureCodes(nextCodes);
        commitSerialIds(nextIds);
        setCaptureError(null);
        return;
      }

      // Adición: el código nuevo es el último (el panel lo agrega al final).
      const added = nextCodes[nextCodes.length - 1];
      if (!added || !validateSerial) return;
      setCaptureError(null);
      setValidating(true);
      try {
        const res = await validateSerial({
          serialCode: added,
          itemExtensionId,
          storageId,
          lotId,
        });
        if (res?.available) {
          // Tope duro: ya alcanzamos la cantidad requerida → ignorar el sobrante.
          if (required > 0 && serialIdsRef.current.length >= required) {
            // `required` además de `count`, por lo mismo que en SerialSelectPanel: un catálogo
            // de host que declare el texto con `{required}` dejaba el token crudo.
            setCaptureError(t('serial_panel.max_reached', { count: required, required }));
            return;
          }
          setCaptureCodes((prev) => [...prev, added]);
          commitSerialIds([...serialIdsRef.current, res.serialId ?? added]);
        } else {
          setCaptureError(res?.reason || t('serial_panel.not_available_error'));
        }
      } catch {
        setCaptureError(t('serial_panel.not_available_error'));
      } finally {
        // En `finally` y no tras el `await`: la rama del tope duro
        // (`max_reached`) sale por un `return` temprano.
        setValidating(false);
      }
    },
    [captureCodes, validateSerial, itemExtensionId, storageId, lotId, commitSerialIds, required, t],
  );

  const handleSelectQueryChange = useCallback((q: string) => {
    setSelectQuery(q);
    setSelectPage(1);
  }, []);

  // ── Resultado ──────────────────────────────────────────────
  const count = serialIds.length;
  // `>=` (no `===`): tolera el tope. Una sobre-captura/sobre-selección (count >
  // required) NUNCA debe dejar Guardar bloqueado para siempre.
  const isComplete = !isActive || unsupported || count >= required;

  const panelProps = useMemo<SerialPanelProps | null>(() => {
    if (!isActive || unsupported) return null;
    if (mode === 'capture') {
      return {
        mode: 'capture',
        serials: captureCodes,
        required,
        readOnly,
        externalError: captureError,
        validating,
        onChange: handleCaptureChange,
      };
    }
    return {
      mode: 'select',
      rows: selectRows,
      value: serialIds,
      required,
      readOnly,
      pageSize: SELECT_PAGE_SIZE,
      onChange: commitSerialIds,
      query: selectQuery,
      onQueryChange: handleSelectQueryChange,
      page: selectPage,
      onPageChange: setSelectPage,
      total: selectTotal,
      loading: selectLoading,
      confirmOnStatuses,
      confirmMessageKey,
    };
  }, [
    isActive,
    unsupported,
    mode,
    captureCodes,
    required,
    readOnly,
    captureError,
    validating,
    handleCaptureChange,
    selectRows,
    serialIds,
    commitSerialIds,
    selectQuery,
    handleSelectQueryChange,
    selectPage,
    selectTotal,
    selectLoading,
    confirmOnStatuses,
    confirmMessageKey,
  ]);

  return {
    isActive,
    isComplete,
    required,
    count,
    serialIdsField,
    unsupported,
    unsupportedMessage: unsupported ? t('serial_panel.unsupported') : null,
    panelProps,
  };
}
